import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

/**
 * ErrorScreen Component
 * 
 * Full-screen error state for "Not found", "Access denied", etc.
 * 
 * Usage:
 * <ErrorScreen 
 *   type="notFound" 
 *   message="Quiz nicht gefunden" 
 *   backTo="/dashboard"
 * />
 */

// Predefined error types with icons and default messages
const errorTypes = {
  notFound: {
    icon: '🔍',
    title: 'Nicht gefunden',
    defaultMessage: 'Die angeforderte Seite wurde nicht gefunden.'
  },
  accessDenied: {
    icon: '🔒',
    title: 'Zugriff verweigert',
    defaultMessage: 'Du hast keine Berechtigung für diese Seite.'
  },
  error: {
    icon: '⚠️',
    title: 'Fehler',
    defaultMessage: 'Ein Fehler ist aufgetreten.'
  },
  offline: {
    icon: '📡',
    title: 'Keine Verbindung',
    defaultMessage: 'Bitte überprüfe deine Internetverbindung.'
  },
  empty: {
    icon: '📭',
    title: 'Keine Daten',
    defaultMessage: 'Es sind keine Daten vorhanden.'
  }
};

export const ErrorScreen = ({
  type = 'error',
  icon = null,
  title = null,
  message = null,
  backTo = null,
  backLabel = 'Zurück',
  onBack = null,
  action = null,
  background = 'light',
  className = ''
}) => {
  const navigate = useNavigate();
  
  const errorConfig = errorTypes[type] || errorTypes.error;
  const displayIcon = icon || errorConfig.icon;
  const displayTitle = title || errorConfig.title;
  const displayMessage = message || errorConfig.defaultMessage;

  const bgClasses = {
    light: 'bg-gray-50 dark:bg-gray-900',
    dark: 'bg-gray-100 dark:bg-gray-900'
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`min-h-screen ${bgClasses[background]} flex items-center justify-center theme-transition ${className}`}>
      <div className="text-center px-4 max-w-md">
        <div className="text-6xl mb-6">{displayIcon}</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {displayTitle}
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-8">
          {displayMessage}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {(backTo || onBack) && (
            <button
              onClick={handleBack}
              className="px-6 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg theme-transition"
            >
              ← {backLabel}
            </button>
          )}
          {action}
        </div>
      </div>
    </div>
  );
};

ErrorScreen.propTypes = {
  type: PropTypes.oneOf(['notFound', 'accessDenied', 'error', 'offline', 'empty']),
  icon: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string,
  backTo: PropTypes.string,
  backLabel: PropTypes.string,
  onBack: PropTypes.func,
  action: PropTypes.node,
  background: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string
};

export default ErrorScreen;
